/**
 * API Logs Page
 *
 * Manifold observability: API request volume, error rates, top endpoints,
 * and status code breakdown from payments.log_requests.
 */

import { useState, useEffect, useCallback } from 'react'
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts'
import { manifoldAPI } from '../services/api'
import {
  Card, CardContent, CardHeader, CardTitle,
  Button
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, Activity, AlertTriangle, Server, Zap
} from 'lucide-react'

function formatNumber(value) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-US').format(value)
}

export default function ApiLogsPage() {
  const [days, setDays] = useState(30)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await manifoldAPI.getApiLogs({ days })
      if (response.data.success) {
        setData(response.data.data)
      }
    } catch (err) {
      console.error('[API_LOGS] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const summary = data?.summary

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">API Logs</h1>
          <p className="text-sm text-muted-foreground mt-1">Manifold API request volume, error rates, and endpoint analytics</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <Activity className="w-3.5 h-3.5" /> Total Requests
                </div>
                <div className="text-xl font-bold">{formatNumber(summary?.total_requests)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <Zap className="w-3.5 h-3.5" /> Avg/Day
                </div>
                <div className="text-xl font-bold">
                  {summary?.total_requests && days ? formatNumber(Math.round(summary.total_requests / days)) : '-'}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Client Errors (4xx)
                </div>
                <div className="text-xl font-bold text-amber-600">
                  {formatNumber((summary?.total_errors || 0) - (summary?.server_errors || 0))}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <Server className="w-3.5 h-3.5 text-red-500" /> Server Errors (5xx)
                </div>
                <div className="text-xl font-bold text-red-600">{formatNumber(summary?.server_errors)}</div>
                <div className="text-xs text-muted-foreground">{summary?.error_rate}% error rate</div>
              </CardContent>
            </Card>
          </div>

          {/* Daily Volume Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Daily Request Volume</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.daily}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={2} dot={false} name="Total" />
                    <Line type="monotone" dataKey="errors" stroke="#ef4444" strokeWidth={2} dot={false} name="Errors" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* By Status Code */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">By Status Code</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.by_status_code}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="status_code" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar
                        dataKey="count"
                        name="Requests"
                        fill="#6366f1"
                        shape={(props) => {
                          const sc = props.status_code || props.payload?.status_code
                          let fill = '#22c55e'
                          if (sc >= 400 && sc < 500) fill = '#f59e0b'
                          if (sc >= 500) fill = '#ef4444'
                          return <rect {...props} fill={fill} />
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* By HTTP Method */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">By HTTP Method</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {data.by_method?.map((m) => {
                    const pct = summary?.total_requests > 0 ? (m.count / summary.total_requests) * 100 : 0
                    return (
                      <div key={m.method}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-mono font-medium">{m.method}</span>
                          <span className="text-muted-foreground">{formatNumber(m.count)} ({pct.toFixed(1)}%)</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-primary rounded-full h-2 transition-all"
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Endpoints */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top Endpoints</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="pb-2 font-medium text-muted-foreground">Method</th>
                      <th className="pb-2 font-medium text-muted-foreground">Endpoint</th>
                      <th className="pb-2 font-medium text-muted-foreground text-right">Requests</th>
                      <th className="pb-2 font-medium text-muted-foreground text-right">Errors</th>
                      <th className="pb-2 font-medium text-muted-foreground text-right">Error Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_endpoints?.map((ep, i) => (
                      <tr key={i} className="border-b border-border/50">
                        <td className="py-2">
                          <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${
                            ep.method === 'GET' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                            ep.method === 'POST' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                            ep.method === 'PUT' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' :
                            ep.method === 'DELETE' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                            'bg-muted text-foreground'
                          }`}>
                            {ep.method}
                          </span>
                        </td>
                        <td className="py-2 font-mono text-xs truncate max-w-xs">{ep.endpoint}</td>
                        <td className="py-2 text-right">{formatNumber(ep.count)}</td>
                        <td className="py-2 text-right text-red-600">{ep.errors > 0 ? formatNumber(ep.errors) : '-'}</td>
                        <td className="py-2 text-right">
                          <span className={ep.error_rate > 10 ? 'text-red-600 font-medium' : ep.error_rate > 0 ? 'text-amber-600' : ''}>
                            {ep.error_rate > 0 ? `${ep.error_rate}%` : '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
